// src/pages/Agents/RouterAgentModal.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import backendURL from '../../config';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, CircularProgress, Alert } from '@mui/material';

// Default prompts for the router agent
const DEFAULT_ROUTER_GOAL = `Analyze the user's query and classify its primary intent. Respond with 'DATABASE' for data-related questions, 'CALENDAR' for scheduling or event management, or 'GENERAL' for other types of requests.`;
const DEFAULT_ROUTER_BACKSTORY = `You are an intelligent request routing system. Your job is to quickly understand what the user wants and direct their query to the correct specialized capability.`;
const DEFAULT_ROUTER_DESCRIPTION = `Analyze the following user query: '{user_query}'.
Based on the query, classify the user's intent.
If the query is about fetching data, information from a database, products, stock, prices, etc., classify it as 'DATABASE'.
If the query is about booking appointments, scheduling meetings, managing calendar events, etc., classify it as 'CALENDAR'.
For any other type of query, classify it as 'GENERAL'.
Your final answer MUST be a single word: DATABASE, CALENDAR, or GENERAL.`;

export default function RouterAgentModal({ open, onClose }) {
    const [formData, setFormData] = useState({ 
        goal: '', 
        backstory: '', 
        task_description: '' 
    });
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchRouterConfig = useCallback(async () => {
        if (!open) return; // Don't fetch if the modal is not open
        setLoading(true);
        setMessage('');
        setError('');
        const token = localStorage.getItem("access_token");
        try {
            const response = await axios.get(`${backendURL}/api/agents/router/config`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            if (response.data.success && Object.keys(response.data.data).length > 0) {
                const fetchedData = response.data.data;
                setFormData({
                    goal: fetchedData.goal || DEFAULT_ROUTER_GOAL,
                    backstory: fetchedData.backstory || DEFAULT_ROUTER_BACKSTORY,
                    task_description: fetchedData.task_description || DEFAULT_ROUTER_DESCRIPTION,
                });
            } else {
                setFormData({
                    goal: DEFAULT_ROUTER_GOAL,
                    backstory: DEFAULT_ROUTER_BACKSTORY,
                    task_description: DEFAULT_ROUTER_DESCRIPTION,
                });
            }
        } catch (err) {
            setError("Failed to fetch router agent configuration.");
        } finally {
            setLoading(false);
        }
    }, [open]);

    useEffect(() => {
        fetchRouterConfig();
    }, [fetchRouterConfig]);

    const handleSave = async () => {
        setIsProcessing(true);
        setMessage('');
        setError('');
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.post(`${backendURL}/api/agents/router/config`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage(response.data.message);
            setTimeout(() => { // Show success message then close
                onClose();
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration.");
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit Router Agent Configuration</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Agent Goal</Typography>
                        <TextField name="goal" multiline rows={3} fullWidth value={formData.goal} onChange={handleChange} sx={{ mb: 3 }}/>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Agent Backstory</Typography>
                        <TextField name="backstory" multiline rows={3} fullWidth value={formData.backstory} onChange={handleChange} sx={{ mb: 3 }}/>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Task Description
                            <span style={{ color: '#757575', marginLeft: '8px' }}>(must include '{`{user_query}`}')</span>
                        </Typography>
                        <TextField name="task_description" multiline rows={6} fullWidth value={formData.task_description} onChange={handleChange}/>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                {message && <Alert severity="success" sx={{ width: '100%', mr: 2 }}>{message}</Alert>}
                <Button onClick={onClose} disabled={isProcessing}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={isProcessing || loading}>
                    {isProcessing ? 'Saving...' : 'Save Configuration'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}